/**
 * Annotation Stats Page
 * Display statistics and export golden test cases
 */

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BarChart3, AlertCircle, Download, Database, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { StatsOverview, useAnnotationStats, useExportGolden } from '@/features/annotations';
import type { GoldenExportResponse } from '@/types/annotation';
import { useI18n } from '@/i18n';

// Sub-navigation tabs - labels resolved at render time
const TAB_PATHS = [
  { path: '/annotation/batches', key: 'batchManagement', icon: FolderOpen },
  { path: '/annotation/export', key: 'dataExport', icon: Database },
  { path: '/annotation/stats', key: 'statsReport', icon: BarChart3 },
];

export function AnnotationStatsPage() {
  const { t } = useI18n();
  const location = useLocation();
  const [exportResult, setExportResult] = useState<GoldenExportResponse | null>(null);

  const { data: stats, isLoading, error } = useAnnotationStats();
  const exportMutation = useExportGolden();

  const handleExportGolden = async () => {
    try {
      const result = await exportMutation.mutateAsync({
        batch_ids: [], // Export all batches
        min_confidence: null,
        include_notes: false,
      });
      setExportResult(result);
    } catch (error) {
      console.error('Export failed:', error);
      alert(t('annotationStatsPage.exportFailed') + ': ' + (error as Error).message);
    }
  };

  const handleDownload = () => {
    if (!exportResult) return;

    // Create downloadable JSON file
    const jsonContent = JSON.stringify(exportResult, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `golden_cases_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('annotationBatchesPage.pageTitle')}</h1>

        {/* Sub-navigation Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200">
          {TAB_PATHS.map((tab) => {
            const Icon = tab.icon;
            const isActive = location.pathname === tab.path;
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`
                  flex items-center gap-2 px-4 py-2 -mb-px border-b-2 transition-colors
                  ${isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {t(`annotationBatchesPage.tabs.${tab.key}`)}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Page Description */}
      <div className="mb-6">
        <p className="text-gray-600">{t('annotationStatsPage.pageDescription')}</p>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-red-800 font-semibold mb-1">{t('annotationStatsPage.loadFailed')}</h3>
              <p className="text-red-600 text-sm">{(error as Error).message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
            <p className="text-gray-500">{t('annotationStatsPage.loadingStats')}</p>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      {!isLoading && !error && stats && (
        <StatsOverview
          stats={stats}
          onExportGolden={handleExportGolden}
          isExporting={exportMutation.isPending}
        />
      )}

      {/* Export Result Dialog */}
      {exportResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setExportResult(null)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('annotationStatsPage.exportSuccess')}</h3>
            <p className="text-gray-600 mb-4">
              {t('annotationStatsPage.totalCases').replace('{count}', String(exportResult.total_cases))}
            </p>

            {/* Preview */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto">
              <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                {JSON.stringify(exportResult.cases.slice(0, 3), null, 2)}
                {exportResult.cases.length > 3 && '\n' + t('annotationStatsPage.andMore').replace('{count}', String(exportResult.cases.length - 3))}
              </pre>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setExportResult(null)}>
                {t('annotationStatsPage.close')}
              </Button>
              <Button variant="primary" onClick={handleDownload}>
                <Download className="w-4 h-4 mr-2" />
                {t('annotationStatsPage.downloadJson')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
